import _ from "lodash";
import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { Card } from "@mantine/core";
import { getActionableAppointments } from "../../../data/actions/appointmentActions";
import { GenericTransport } from "../../../data/api/baseApi";
import { IState } from "../../../data/reducers/state";
import { IAppointment } from "../../../types/appointments/Appointment";
import { IAssociation } from "../../../types/association/Association";
import { IPerson } from "../../../types/persons/person";
import { IShift, IShiftSession } from "../../../types/shifts/Shift";
import { toDateTime } from "../../../util/date+helper";
import { ShiftSessionLink } from "../../common/Links";
import { CenteredLoader } from "../../common/loader";

interface IActionableAppointmentsProps {
  shiftsMap: { [id: string]: IShift };
  shiftsSessionMap: { [id: string]: IShiftSession };
  associationsMap: { [id: string]: IAssociation };
  personsMap: { [id: string]: IPerson };
  actions: {
    getActionableAppointments: () => Promise<GenericTransport<undefined>>;
  };
}

interface IActionableAppointmentsState {
  actionableAppointments?: {
    [associationId: string]: {
      [shiftId: string]: {
        [sessionId: string]: IAppointment<any>[];
      };
    };
  };
  loadError?: string;
}

class ActionableAppointments extends React.PureComponent<
  IActionableAppointmentsProps,
  IActionableAppointmentsState
> {
  constructor(props: IActionableAppointmentsProps, context) {
    super(props, context);

    this.state = {};

    props.actions
      .getActionableAppointments()
      .then((resp) => {
        const retval = {};
        const inter = _.groupBy(resp.appointments, "shiftSession");
        const { props } = this;
        for (const shiftSession of Object.keys(inter)) {
          const appointment = inter[shiftSession][0];
          const associationId = appointment.association;
          const shiftSessionObj =
            props.shiftsSessionMap[appointment.shiftSession];
          const shiftId = shiftSessionObj?.shift;
          if (!shiftId) continue;

          retval[appointment.association] = {
            ...(retval[associationId] || {}),
            [shiftId]: {
              ...(retval[associationId]?.[shiftId] || {}),
              [shiftSession]: inter[shiftSession],
            },
          };
        }

        this.setState({ actionableAppointments: retval });
      })
      .catch((er) => this.setState({ loadError: er.toString() }));
  }

  render() {
    if (!!this.state.loadError && !this.state.actionableAppointments) {
      return (
        <Card padding="md">
          <p>
            An error occurred when getting actionable appointments:{" "}
            {this.state.loadError}
          </p>
        </Card>
      );
    }
    if (!this.state.actionableAppointments) {
      return <CenteredLoader />;
    }
    const ap = this.state.actionableAppointments;
    if (Object.keys(ap).length == 0) return <></>;
    return (
      <>
        {Object.keys(ap).map((assocId) => (
          <Card
            key={`actionable-card-${assocId}`}
            style={{ padding: "20px", marginBottom: "20px" }}
          >
            <h5 style={{ marginBottom: "20px" }}>
              {this.props.associationsMap[assocId]?.name?.split(" ")[0]} -
              Actionable appointments
            </h5>
            {Object.keys(ap[assocId]).map((shftId, idx) => (
              <div key={shftId}>
                {idx !== 0 && <hr />}
                <p>
                  <b>{this.props.shiftsMap[shftId]?.name}</b>
                </p>
                <div style={{ marginLeft: "20px" }}>
                  {Object.keys(ap[assocId][shftId]).map((sessionId) => {
                    const appointments = ap[assocId][shftId][sessionId];
                    const shiftSession = this.props.shiftsSessionMap[sessionId];
                    const s = appointments.length == 1 ? "" : "s";
                    if (!shiftSession) {
                      return <></>;
                    }
                    return (
                      <div key={sessionId}>
                        <ShiftSessionLink
                          shiftId={shftId}
                          sessionId={sessionId}
                        >
                          <b>
                            {toDateTime(shiftSession.start).toLocaleString(
                              DateTime.DATETIME_SHORT,
                            )}
                          </b>
                          :{" "}
                          <span
                            style={{
                              color:
                                "var(--official-cell-bg-rejectedAppointment)",
                            }}
                          >
                            {appointments.length} appointment
                            {s} to action
                          </span>
                        </ShiftSessionLink>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </Card>
        ))}
      </>
    );
  }
}

const mapStateToProps = (
  state: IState,
  props: IActionableAppointmentsProps,
) => ({
  shiftsMap: state.shifts.map,
  shiftsSessionMap: state.shiftSessions.map,
  associationsMap: state.associations.map,
  personsMap: state.persons.map,
});

function mapDispatchToProps(dispatch): Partial<IActionableAppointmentsProps> {
  return {
    actions: {
      getActionableAppointments: () => dispatch(getActionableAppointments()),
    },
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ActionableAppointments);
